import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Menu, MenuItem, Typography } from '@mui/material';
import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteFilledIcon from '../../core-components/assets/icons/DeleteIconFilled';
import EditIcon from '../../core-components/assets/icons/EditIcon';
import { IconButton } from '../../core-components/theme/components/Buttons';
import { rowMenuItemSx, rowMenuListSx, rowMenuPaperSx } from '../../core-components/theme/components/Table/rowMenuStyles';

export interface RowActionAccess {
    readonly canEdit: boolean;
    readonly canDelete: boolean;
}

export interface RowModalProps<Row> {
    readonly data: Row;
    readonly handleClose: () => void;
}

export interface TableActionsProps<Row> extends RowActionAccess {
    readonly rowData: Row;
    readonly DeleteComponent?: React.ComponentType<RowModalProps<Row>>;
    readonly EditComponent?: React.ComponentType<RowModalProps<Row>>;
    readonly deleteAction?: (row: Row) => void;
    readonly editAction?: (row: Row) => void;
    readonly onEditIntent?: (row: Row) => void;
    readonly customMenuItems?: readonly React.ReactNode[];
    readonly disabled?: boolean;
    readonly children?: React.ReactNode;
}

type OpenModal = 'edit' | 'delete' | null;

const stopRowClick = (event: React.SyntheticEvent) => event.stopPropagation();

export default function TableActions<Row>({
    rowData,
    DeleteComponent,
    EditComponent,
    deleteAction,
    canDelete,
    editAction,
    onEditIntent,
    canEdit,
    customMenuItems,
    disabled,
    children,
}: TableActionsProps<Row>) {
    const { t } = useTranslation();
    const ids = useId();
    const buttonId = `${ids}-button`;
    const menuId = `${ids}-menu`;
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [openModal, setOpenModal] = useState<OpenModal>(null);

    const editable = canEdit && Boolean(EditComponent || editAction);
    const deletable = canDelete && Boolean(DeleteComponent || deleteAction);
    const hasCustomItems = (customMenuItems?.length ?? 0) > 0 || React.Children.count(children) > 0;
    if (!editable && !deletable && !hasCustomItems) return null;

    const menuOpen = menuAnchorEl !== null;

    const openMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const closeMenu = (event: React.SyntheticEvent) => {
        event.stopPropagation();
        setMenuAnchorEl(null);
    };

    const closeModal = () => setOpenModal(null);

    const handleDelete = (event: React.MouseEvent<HTMLElement>) => {
        closeMenu(event);
        if (deleteAction) deleteAction(rowData);
        else setOpenModal('delete');
    };

    const handleEdit = (event: React.MouseEvent<HTMLElement>) => {
        closeMenu(event);
        if (editAction) editAction(rowData);
        else setOpenModal('edit');
    };

    return (
        <React.Fragment>
            <IconButton
                id={buttonId}
                label={t('common.rowActions', 'Row actions')}
                onClick={openMenu}
                disabled={disabled}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-controls={menuOpen ? menuId : undefined}
            >
                <MoreVertIcon />
            </IconButton>
            <Menu
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={closeMenu}
                slotProps={{ paper: { sx: rowMenuPaperSx } }}
                MenuListProps={{ id: menuId, 'aria-labelledby': buttonId, sx: rowMenuListSx }}
            >
                {editable && (
                    <MenuItem
                        sx={rowMenuItemSx}
                        onClick={handleEdit}
                        onMouseEnter={() => onEditIntent?.(rowData)}
                        onFocus={() => onEditIntent?.(rowData)}
                    >
                        <EditIcon />
                        <Typography ml={2} color="Neutrals.500">
                            {t('common.edit', 'Edit')}
                        </Typography>
                    </MenuItem>
                )}
                {deletable && (
                    <MenuItem sx={rowMenuItemSx} onClick={handleDelete}>
                        <DeleteFilledIcon />
                        <Typography ml={2} color="Neutrals.500">
                            {t('common.delete', 'Delete')}
                        </Typography>
                    </MenuItem>
                )}
                {customMenuItems}
                {children}
            </Menu>
            {openModal === 'edit' && EditComponent && (
                <Box onClick={stopRowClick}>
                    <EditComponent data={rowData} handleClose={closeModal} />
                </Box>
            )}
            {openModal === 'delete' && DeleteComponent && (
                <Box onClick={stopRowClick}>
                    <DeleteComponent data={rowData} handleClose={closeModal} />
                </Box>
            )}
        </React.Fragment>
    );
}
